import { useGetConfigurationKeys } from '../graphql/useGetConfigurationKeys';
import React, { useMemo, useState } from 'react';
import { Select, Sidebar, type SidebarData } from '../../../../components';
import { useGetConfigurationKey } from '../graphql/useGetConfigurationKey';
import { ConfigurationDetail } from './ConfigurationDetail';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import {
  EmailConfigurationDetail,
  EmailManagement,
} from './EmailTemplateDetail';
import { EmailDryRun } from './EmailDryRun';

export type ConfigurationListProps = {
  ignoredConfigurationList?: string[];
};

const ConfigurationPage: React.FC = () => {
  const {
    keys,
    loading,
    // error,
    // refetch
  } = useGetConfigurationKeys();
  const [activated, setActivated] = useState<string>(keys?.[0] ?? '');

  const {
    config,
    loading: loadingConfig,
    // error: errorConfig,
    refetch: refetchConfig,
  } = useGetConfigurationKey(activated ?? null);

  if (loading || loadingConfig) {
    return <div>Loading...</div>;
  }

  const getSideBarData = (): SidebarData[] => {
    return (keys ?? []).map((key: string) => ({
      id: key,
      label: key,
      name: key,
    }));
  };

  return (
    <div className="w-[100%]">
      <div className="flex flex-row">
        <Sidebar
          sidebarData={getSideBarData()}
          onChangeActivated={setActivated}
          manuallyActivatedId={activated}
        />
        <div className="w-[100%]">
          <ConfigurationDetail {...config} refetch={refetchConfig} />
        </div>
      </div>
    </div>
  );
};

const EmailConfigurationList: React.FC = () => {
  const {
    data,
    loading,
    // error
  } = useQuery(gql`
    query {
      getEmailTemplates {
        subject
        content
        tags
        displayName
        templateId
        placeholder
      }
    }
  `);

  const { getEmailTemplates } = data ?? { getEmailTemplates: [] };

  const processName = (name: string): React.ReactNode => {
    if (name.length > 35) {
      return <span title={name}>{name.slice(0, 35) + '...'}</span>;
    }
    return <p>{name}</p>;
  };

  const sideBarData = useMemo(() => {
    return (
      getEmailTemplates?.map((template: any) => ({
        id: template.templateId,
        label: processName(template.displayName),
        name: processName(template.displayName),
      })) ?? []
    );
  }, [getEmailTemplates, loading]);

  const [activateId, setActivateId] = useState<string>(
    sideBarData?.[0]?.id ?? '',
  );
  const selectedTemplate =
    getEmailTemplates?.find(
      (template: any) => template.templateId === activateId,
    ) ?? {};

  if (loading) {
    return <div>Loading...</div>;
  }
  return (
    <div className="w-[100%]">
      <div className="flex flex-row">
        <Sidebar
          sidebarData={sideBarData}
          onChangeActivated={setActivateId}
          manuallyActivatedId={activateId}
        />
        <div className="flex flex-col p-4">
          <div className="w-[100%]">
            <EmailConfigurationDetail {...selectedTemplate} />
            <EmailDryRun selectedTemplate={selectedTemplate} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const ConfigurationList: React.FC<ConfigurationListProps> = () => {
  const [selected, setSelected] = useState<string>('configuration-keys');

  const selectOptions = [
    {
      label: 'Configuration Keys',
      value: 'configuration-keys',
    },
    {
      label: 'Email Templates',
      value: 'email-templates',
    },
  ];

  const renderSelection = useMemo(() => {
    if (selected === 'configuration-keys') {
      return <ConfigurationPage />;
    } else if (selected === 'email-templates') {
      return (
        <div>
          <EmailConfigurationList />
          <EmailManagement />
        </div>
      );
    } else {
      return <div>Not Found</div>;
    }
  }, [selected]);

  return (
    <div>
      <Select
        options={selectOptions}
        value={selected}
        onChange={e => {
          setSelected(e.target.value);
        }}
      />
      <div>{renderSelection}</div>
    </div>
  );
};
